// FT-153 REQ-07 / REQ-09 — повтор подтверждения и ограничение параллельности.
//
// Почему это отдельный модуль, а не десять строк внутри uppy_checkin.vue: его поведение
// проверяется изолированно, без поднятия Uppy и компонента. Пакет FT-153 прошёл пять
// раундов ревью, и каждый находил один и тот же класс дефекта — утверждение о поведении
// кода, которое никто не запускал. Здесь запускать есть что.

// Сколько подтверждений одновременно держат тред api.
//
// Раньше здесь было написано, что это «то же самое» ограничение, что и параллельность
// отправки в S3. Это неверно, и замер на стенде 14.09 показал почему: части уезжают
// PUT'ом НАПРЯМУЮ в S3 и наших слотов не трогают, а в api на файл приходит около шести
// коротких запросов независимо от параллельности транспорта. Числа развязаны: транспорт
// живёт в TRANSPORT_LIMIT (uppy_checkin.vue), здесь — только подтверждения.
//
// Слотов на весь прод двенадцать: два хоста × WEB_CONCURRENCY 2 × RAILS_MAX_THREADS 3.
// Подтверждение синхронно ждёт HEAD в S3, поэтому пачка из тридцати, отправленная разом,
// способна выбрать их все — и именно тогда, когда хранилище деградирует, то есть когда
// приложение обязано оставаться живым.
export const CONFIRM_LIMIT = 3;

// Три попытки: сразу, через секунду, через три. Байты уже в бакете — повторяем дешёвый
// запрос, а не перезаливаем файл.
export const CONFIRM_RETRY_DELAYS = [0, 1000, 3000];

/**
 * Стоит ли повторять подтверждение при таком статусе ответа.
 *
 * 4xx детерминированы: чужой ключ, объект не найден, объект свыше потолка. Повтор их не
 * вылечит и только потратит ещё один HEAD.
 *
 * 503 наш эндпоинт отдаёт, когда деградировало хранилище — то есть прямо сообщает, что
 * проблема на его стороне. Повторять втрое значит бить по тем же двенадцати слотам в
 * момент, когда им и так плохо. Сотрудник повторит вручную, когда хранилище поднимется;
 * файл к этому времени всё ещё лежит в бакете.
 */
export function isRetryableStatus(status) {
  return status >= 500 && status !== 503;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Повторяет ПОДТВЕРЖДЕНИЕ, а не загрузку.
 *
 * Отказ подтверждения при уже уехавших байтах — самый обидный из отказов: файл в бакете,
 * а в брони его нет. Прежняя редакция на этом месте сбрасывала состояние загрузки, и
 * штатная кнопка повтора пересылала файл целиком — под тремястами мегабайтами это ровно
 * тот исход, против которого фича и существует.
 *
 * @param {() => Promise<Response>} doFetch запрос подтверждения; бросает на сетевом отказе
 * @param {{delays?: number[], signal?: AbortSignal, shouldContinue?: () => boolean}} options
 *   shouldContinue проверяется ПЕРЕД КАЖДОЙ попыткой, а не однажды на входе: файл могли
 *   снять, пока шла пауза, и тогда повтор уходил бы на сервер, а при успехе заводил строку,
 *   которой в интерфейсе нет (находка ревью раунда 6).
 * @returns {Promise<Response>} успешный ответ
 * @throws последняя ошибка, если попытки исчерпаны или отказ детерминирован
 */
export async function confirmWithRetries(
  doFetch,
  { delays = CONFIRM_RETRY_DELAYS, signal, shouldContinue } = {}
) {
  let lastError;

  // Ушли со страницы или сняли файл — добивать сервер незачем.
  const stillWanted = () => {
    if (signal && signal.aborted) return false;
    return shouldContinue ? Boolean(shouldContinue()) : true;
  };

  for (let attempt = 0; attempt < delays.length; attempt += 1) {
    if (!stillWanted()) break;
    if (delays[attempt]) await sleep(delays[attempt]);
    if (!stillWanted()) break;

    let response;
    try {
      response = await doFetch();
    } catch (networkError) {
      lastError = networkError;
      continue;
    }

    if (response.ok) return response;

    const error = new Error(`confirm ${response.status}`);
    if (!isRetryableStatus(response.status)) throw error;
    lastError = error;
  }

  throw lastError || new Error("confirm aborted");
}

/**
 * Семафор, который живёт ДОЛЬШЕ одной пачки.
 *
 * Раньше здесь была функция `runWithLimit(items, limit, fn)`, создававшая пул воркеров на
 * каждый вызов. Ревью раунда 6 показало зондом, чем это плохо: при `allowMultipleUploadBatches`
 * вторая выбранная пачка входит в постпроцессор, пока первая ещё подтверждается, и заводит
 * СВОЙ пул — наблюдалось шесть одновременных подтверждений при обещанных трёх. Обещание
 * «не больше трёх» держится, только если ограничитель один на компонент, а не на вызов.
 *
 * Изоляция задач сознательно НЕ здесь: она у вызывающего, потому что только он знает, что
 * делать с конкретным упавшим файлом. `run` честно пробрасывает отказ.
 *
 * @param {number} limit сколько задач выполняется одновременно
 * @returns {{run: (fn: () => Promise<any>) => Promise<any>, active: number}}
 */
export function createLimiter(limit) {
  // Нулевой и отрицательный лимит раньше давали НОЛЬ воркеров: очередь не разбиралась,
  // Promise.all([]) резолвился, и задачи молча не выполнялись ни одна.
  const max = Math.max(1, Math.floor(limit) || 1);
  const waiting = [];
  let active = 0;

  const release = () => {
    active -= 1;
    const next = waiting.shift();
    if (next) next();
  };

  return {
    get active() {
      return active;
    },
    run(fn) {
      return new Promise((resolve, reject) => {
        const start = () => {
          active += 1;
          Promise.resolve()
            .then(fn)
            .then(
              (value) => { release(); resolve(value); },
              (error) => { release(); reject(error); }
            );
        };

        if (active < max) start();
        else waiting.push(start);
      });
    },
  };
}
